import { message } from 'antd';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  trackMixpanel,
  validateBeneficiaryAccountName,
} from '@monorepo/client-common';
import { PayoutPayload, WithdrawPayload } from '@monorepo/client-types';
import { useGetFeatureFlag } from '@monorepo/feature-flag/client';
import { DialogHandle } from '@monorepo/nova-ui';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import {
  AirwallexBeneficiaryFormOptions,
  EventTypes,
  MAX_CRYPTO_WITHDRAWAL_LIMIT,
  PaymentMethod,
  PayoutMetadata,
  PayoutSource,
  PayoutStatus,
} from '@monorepo/types';
import {
  ElementTypes,
  Payouts as AirwallexPayouts,
} from '@airwallex/components-sdk';
import { DashboardContext } from '@hub/features/account-dashboard/contexts/dashboard-context';
import { useStore } from '@hub/helpers/use-store';
import { EnvService } from '@hub/services/core/env-service';
import { PayoutStage, PayoutSuccessBehavior } from '../types';

interface UsePayoutSubmissionProps {
  source: PayoutSource;
  sourceId: string;
  maxWithdrawal: number;
  showAmountSelection?: boolean;
  validateWithDrawEligible: () => Promise<void>;
  modalRef: React.RefObject<DialogHandle | null>;
  form: {
    getValues: () => WithdrawPayload;
  };
  isAffiliate?: boolean;
}

/**
 * Payout submission for withdraw-funds-nova.
 *
 * **Non-affiliate (account, etc.):** Single step — submit runs Airwallex (if needed), then creates the
 * payout request, shows toast, closes the dialog. No intermediate stages.
 *
 * **Affiliate:** Staged flow — (1) form → (2) confirmation modal → (3) success screen. React Hook Form
 * state survives stage changes, but the Airwallex embedded widget does not: `beneficiaryFormRef.submit()`
 * must run while the widget is mounted. So for affiliate + bank transfer we create the beneficiary and
 * store `beneficiaryId` in state *before* showing the confirmation step; the confirm action only calls
 * `createPayoutRequest` using that metadata.
 */
export const usePayoutSubmission = ({
  source,
  sourceId,
  maxWithdrawal,
  showAmountSelection,
  validateWithDrawEligible,
  modalRef,
  form,
  isAffiliate = false,
}: UsePayoutSubmissionProps) => {
  const { isLoading, updateLoadingState } = useLoading();
  const dashboardContext = useContext(DashboardContext);
  const updateAccount = dashboardContext?.updateAccount;
  const { t } = useTranslation(['withdrawFundsNova', 'errors']);
  const enabledWithdrawMechanism = useGetFeatureFlag(
    'enable-revert-withdrawal-mechanism',
    false,
    {
      env: EnvService.application,
    },
  );

  const {
    dataStore: { payoutStore, userStore },
  } = useStore();

  const beneficiaryFormRef = useRef<
    ElementTypes['beneficiaryForm'] | undefined
  >(undefined);

  /** Affiliate only: `Form` → `Confirmation` → `Success`. Non-affiliate stays on `Form` (UI ignores stage). */
  const [payoutStage, setPayoutStage] = useState<PayoutStage>(PayoutStage.Form);

  /** Affiliate only: amount + method captured when leaving the form step (confirmation shows copy, not full form). */
  const [payoutData, setPayoutData] = useState<{
    amount: number;
    paymentMethod: PaymentMethod;
  } | null>(null);

  /**
   * Affiliate + Airwallex only: `metadata.beneficiaryId` after `createAirwallexBeneficiary`, saved before
   * unmounting the Airwallex UI so `handleConfirmPayout` can POST the payout without calling `submit()` again.
   */
  const [preparedAirwallexMetadata, setPreparedAirwallexMetadata] = useState<
    PayoutMetadata | undefined
  >(undefined);

  /** SDK submit → API beneficiary → mutates `payoutPayload.metadata.beneficiaryId`. */
  const handleAirwallexWithdraw = async (payoutPayload: PayoutPayload) => {
    if (!beneficiaryFormRef.current) {
      message.error(t('errors:airwallex_withdraw_error'), 3);
      throw new Error('Airwallex form not found');
    }
    try {
      const submitResponse: AirwallexPayouts.BeneficiaryFormSubmitResponse =
        await beneficiaryFormRef.current.submit();

      if (submitResponse.errors) {
        message.error(t('errors:error_submitting_form'), 3);
        throw new Error('Error submitting form');
      }
      try {
        validateBeneficiaryAccountName(submitResponse.values);
      } catch (e) {
        message.error(t('errors:account_name_digits_only'));
        throw e;
      }

      const beneficiaryFormPayload: AirwallexBeneficiaryFormOptions =
        submitResponse.values;

      const beneficiary = await payoutStore.createAirwallexBeneficiary(
        beneficiaryFormPayload,
      );

      if (!beneficiary) {
        message.error(t('errors:create_payout_error'));
        throw new Error('Error creating beneficiary');
      }

      payoutPayload.metadata = {
        ...payoutPayload.metadata,
        beneficiaryId: beneficiary.id,
      };

      return beneficiary;
    } catch (e) {
      updateLoadingState(LoadingState.Error);
      throw e;
    }
  };

  /**
   * POST payout request. On success, either close the dialog (non-affiliate) or move to success stage
   * (affiliate). Returns whether the payout was created as pending.
   */
  const completePayoutRequest = async (
    payoutPayload: PayoutPayload,
    successBehavior: PayoutSuccessBehavior,
  ): Promise<boolean> => {
    try {
      const response = await payoutStore.createPayoutRequest({
        ...payoutPayload,
      });

      if (response?.status === PayoutStatus.Pending) {
        // Non-affiliate: toast + close. Affiliate: eligibility refresh + dedicated success UI in the dialog.
        if (successBehavior === PayoutSuccessBehavior.CloseModal) {
          message.success(
            t('withdrawFundsNova:request_submitted_successfully'),
          );
          await validateWithDrawEligible();
          handleCancel();
        } else {
          await validateWithDrawEligible();
          setPayoutStage(PayoutStage.Success);
        }
        updateLoadingState(LoadingState.Loaded);
        return true;
      }

      const failure = response as {
        status?: unknown;
        message?: string;
      };

      if (
        failure.status === 400 &&
        typeof failure.message === 'string' &&
        failure.message.includes(
          'Amount exceeds maximum crypto withdrawal limit of',
        )
      ) {
        message.error(
          t('errors:withdrawal_limit_dynamic', {
            limit: MAX_CRYPTO_WITHDRAWAL_LIMIT,
          }),
        );
        updateLoadingState(LoadingState.Loaded);
        return false;
      }

      message.error(
        failure.message === 'Amount is higher than available amount'
          ? t('errors:error_amount')
          : t('errors:error_refresh'),
      );
      updateLoadingState(LoadingState.Loaded);
      return false;
    } catch (e) {
      console.error('Failed creating a payout', e);
      message.error(t('errors:create_payout_error'));
      updateLoadingState(LoadingState.Error);
      return false;
    }
  };

  const handleCancel = () => {
    modalRef.current?.close();
  };

  const trackPayoutMixpanel = (payoutPayload: PayoutPayload) => {
    trackMixpanel({
      event: EventTypes.WITHDRAW_ELIGIBILITY_CHECKED,
      properties: {
        email: userStore.currentUser.email,
        country: userStore.currentUser.country,
        platform: EnvService.get().application,
        amount: payoutPayload.amount,
        source: payoutPayload.source,
        sourceId: payoutPayload.sourceId,
        paymentOption: payoutPayload.paymentOption,
        agreedToTerms: payoutPayload.agreedToTerms,
        acknowledgeWithdrawFormAccount:
          payoutPayload.acknowledgeWithdrawFormAccount,
        metadata: payoutPayload.metadata,
      },
    });
  };

  const getPayoutAmount = (): number => {
    const requestedAmount = Number(form.getValues().amount);

    if (Number.isFinite(requestedAmount) && requestedAmount > 0) {
      return requestedAmount;
    }

    return showAmountSelection ? requestedAmount : maxWithdrawal;
  };

  /** Account (and other non-affiliate) path: one-shot submit; Airwallex runs in the same view as the form. */
  const handleSubmitNonAffiliate = async () => {
    updateLoadingState(LoadingState.Loading);

    const formValues = form.getValues();
    const isAirwallex = formValues.paymentOption === PaymentMethod.Airwallex;
    const amount = getPayoutAmount();

    const payoutPayload: PayoutPayload = {
      ...formValues,
      amount,
      source,
      sourceId,
    };

    try {
      if (isAirwallex) {
        try {
          await handleAirwallexWithdraw(payoutPayload);
        } catch (e) {
          console.error('Failed to create Airwallex beneficiary', e);
          updateLoadingState(LoadingState.Error);
          return;
        }
      }

      const ok = await completePayoutRequest(
        payoutPayload,
        PayoutSuccessBehavior.CloseModal,
      );
      if (ok) {
        if (enabledWithdrawMechanism && updateAccount) {
          updateAccount();
        }
        trackPayoutMixpanel(payoutPayload);
        modalRef.current?.close();
      }
    } catch (e) {
      console.error('Failed creating a payout', e);
      message.error(t('errors:create_payout_error'));
      updateLoadingState(LoadingState.Error);
    }
  };

  /**
   * Affiliate path: advance to confirmation. If bank transfer, finish Airwallex + beneficiary API here
   * (widget still mounted); otherwise only reset any stale Airwallex metadata.
   */
  const handleSubmitAffiliate = async () => {
    const formValues = form.getValues();
    const amount = getPayoutAmount();
    const isAirwallex = formValues.paymentOption === PaymentMethod.Airwallex;

    if (isAirwallex) {
      updateLoadingState(LoadingState.Loading);
      const draftPayload: PayoutPayload = {
        ...formValues,
        amount,
        source,
        sourceId,
      };
      try {
        await handleAirwallexWithdraw(draftPayload);
        setPreparedAirwallexMetadata(
          draftPayload.metadata ? { ...draftPayload.metadata } : undefined,
        );
        updateLoadingState(LoadingState.Loaded);
      } catch (e) {
        console.error('Failed to create Airwallex beneficiary', e);
        updateLoadingState(LoadingState.Error);
        return;
      }
    } else {
      setPreparedAirwallexMetadata(undefined);
    }

    setPayoutData({
      amount,
      paymentMethod: formValues.paymentOption,
    });
    setPayoutStage(PayoutStage.Confirmation);
  };

  const handleSubmit = isAffiliate
    ? handleSubmitAffiliate
    : handleSubmitNonAffiliate;

  /**
   * Affiliate confirmation step: build payload from RHF + stored amount + precomputed Airwallex metadata.
   * Does not call `beneficiaryFormRef.submit()` — the widget is unmounted on this stage.
   */
  const handleConfirmPayout = async () => {
    if (!payoutData) return;

    updateLoadingState(LoadingState.Loading);

    const formValues = form.getValues();
    const isAirwallex = formValues.paymentOption === PaymentMethod.Airwallex;

    if (isAirwallex && !preparedAirwallexMetadata) {
      message.error(t('errors:airwallex_withdraw_error'), 3);
      updateLoadingState(LoadingState.Error);
      setPayoutStage(PayoutStage.Form);
      setPayoutData(null);
      return;
    }

    const payoutPayload: PayoutPayload = {
      ...formValues,
      amount: payoutData.amount,
      source,
      sourceId,
      // Merge beneficiaryId (and any other metadata) produced before the confirmation UI.
      metadata: preparedAirwallexMetadata
        ? { ...(formValues.metadata ?? {}), ...preparedAirwallexMetadata }
        : formValues.metadata,
    };

    try {
      const ok = await completePayoutRequest(
        payoutPayload,
        PayoutSuccessBehavior.SuccessStage,
      );
      if (ok) {
        if (enabledWithdrawMechanism && updateAccount) {
          updateAccount();
        }
        trackPayoutMixpanel(payoutPayload);
      } else {
        setPayoutStage(PayoutStage.Form);
        setPayoutData(null);
        setPreparedAirwallexMetadata(undefined);
      }
    } catch (e) {
      console.error('Failed creating a payout', e);
      message.error(t('errors:create_payout_error'));
      updateLoadingState(LoadingState.Error);
      setPayoutStage(PayoutStage.Form);
      setPayoutData(null);
      setPreparedAirwallexMetadata(undefined);
    }
  };

  /** Back from confirmation to form; drop staged data so Airwallex remounts clean if needed. */
  const handleCancelConfirmation = () => {
    setPayoutStage(PayoutStage.Form);
    setPayoutData(null);
    setPreparedAirwallexMetadata(undefined);
  };

  /** After affiliate success screen: close dialog and reset staged state for a future withdrawal. */
  const handleSuccessClose = () => {
    modalRef.current?.close();
    setPayoutStage(PayoutStage.Form);
    setPayoutData(null);
    setPreparedAirwallexMetadata(undefined);
  };

  return {
    beneficiaryFormRef,
    isLoading,
    handleSubmit,
    handleCancel,
    payoutStage,
    payoutData,
    handleConfirmPayout,
    handleCancelConfirmation,
    handleSuccessClose,
  };
};
